/**
 * join-lines.ts — the way in's status, part by part, and each part in words.
 * The card's status (entry-card.tsx, EntryStatus) draws a list of parts for
 * the eye and says the same list through its status line, so what is drawn
 * and what is said come from one list: how the join stands is turned into
 * parts once (joinParts, for the card; the test session's form builds its
 * own), and each part is drawn by its kind there and said by its kind here
 * (partLine), from the same words — a step and how far the join has come, a
 * refusal's title and the line under it, a wait, a line before the press, a
 * match left.
 */

import type { TFunction } from 'i18next';

import type { ConnectionStep } from '../../domains/auth';
import { waitingForStart, type RefusalWords } from '../../domains/join-refusals';
import { STEP_WORDS, STEPS } from './join-steps';
import type { JoinState } from './use-join-state';

/** A part of the card's status: drawn for the eye, and said in words. */
export type StatusPart =
  /** A match left: its head, drawn in the outline beside the status. */
  | { kind: 'left' }
  /** A line before the press: what it does. */
  | { kind: 'hint'; words: string }
  /** A wait with no steps: the link being checked, a session waited for. */
  | { kind: 'wait'; words: string }
  /** The join under way: its step, and how far it has come. */
  | { kind: 'step'; step: ConnectionStep }
  /** A refusal: its title and the line under it. */
  | { kind: 'refusal'; words: RefusalWords };

/** How far a join has come, in words: "3 of 4". */
export function stepOf(step: ConnectionStep, t: TFunction): string {
  return t('entry.stepOf', { n: STEPS.indexOf(step) + 1, total: STEPS.length });
}

/** A refusal as the line says it: its title, and the line under it as a sentence of its own. */
function refusalLine(words: RefusalWords): string {
  if (!words.detail) return words.title;
  return `${words.title}${/[.!?…]$/.test(words.title) ? '' : '.'} ${words.detail}`;
}

/** A part as the status line says it, in the words the card draws it with. */
export function partLine(part: StatusPart, t: TFunction): string {
  switch (part.kind) {
    case 'left':
      return t('entry.left');
    case 'hint':
    case 'wait':
      return part.words;
    case 'step':
      return `${t(STEP_WORDS[part.step])} ${stepOf(part.step, t)}`;
    case 'refusal':
      return refusalLine(part.words);
  }
}

/** How the join stands (use-join-state.ts), as the parts of the card's status. */
export function joinParts(state: JoinState, t: TFunction): StatusPart[] {
  switch (state.kind) {
    case 'left':
      return [{ kind: 'left' }];
    case 'ready':
      return state.hint ? [{ kind: 'hint', words: state.hint }] : [];
    case 'checking':
      return [{ kind: 'wait', words: state.says }];
    case 'joining':
      return [{ kind: 'step', step: state.step }];
    case 'waiting':
      return [
        { kind: 'refusal', words: waitingForStart() },
        { kind: 'wait', words: t('entry.retrying') },
      ];
    case 'refused':
      return [{ kind: 'refusal', words: state.words }];
  }
}
